"use client";

import Image from "next/image";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faOilCan, faCogs, faWrench, faSoap, faCarBattery, faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import { Card, CardContent } from "@/components/ui/card";
import type { SectionItem } from "@/types";

interface ServiceCardProps {
  item: SectionItem;
}

export function ServiceCard({ item }: ServiceCardProps) {
  const getIcon = (title?: string) => {
    switch (title) {
      case "Preventive Maintenance": return faShieldHalved;
      case "Oil Change": return faOilCan;
      case "Chain Lubrication": return faCogs;
      case "Mechanical Repair": return faWrench;
      case "Motorcycle Washing": return faSoap;
      case "Battery Charging": return faCarBattery;
      default: return faPlus;
    }
  };

  const content = (
    <Card className="group h-full overflow-hidden border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl p-0 gap-0">
      <div className="relative h-50 overflow-hidden">
        {item.image?.url ? (
          <Image
            src={item.image.url}
            alt={item.image.alt || item.title || "Service"}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="absolute inset-0 bg-linear-to-br from-orange-400 via-amber-500 to-orange-600" />
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-linear-to-br from-orange-500/80 to-amber-500/80 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="text-4xl text-white">
            <FontAwesomeIcon icon={getIcon(item.title)} />
          </span>
        </div>
      </div>
      <CardContent className="p-5">
        {item.title && (
          <h3 className="mb-2 text-lg font-semibold">{item.title}</h3>
        )}
        {item.description && (
          <p className="mb-3 text-sm text-muted-foreground line-clamp-3">
            {item.description}
          </p>
        )}
        {/* Feature tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {item.tags.map((tag, i) => (
              <span
                key={i}
                className="rounded-md bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-600 dark:bg-orange-950/30 dark:text-orange-400"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const href = item.link || item.linkUrl;
  if (href) {
    return (
      <Link href={href} className="block h-full">
        {content}
      </Link>
    );
  }

  return content;
}
